import { useMemo } from "react";
import { useColorMode } from "./AppThemeContext";
import { isCssVarColor, resolveCssColor } from "./tokens";

const cache = new Map<string, string>();

const resolveCached = (value: string, mode: string) => {
  const key = `${mode}|${value}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const resolved = resolveCssColor(value);
  cache.set(key, resolved);
  return resolved;
};

const deepResolve = <T,>(value: T, mode: string): T => {
  if (typeof value === "string") return (isCssVarColor(value) ? resolveCached(value, mode) : value) as unknown as T;
  if (Array.isArray(value)) return value.map((item) => deepResolve(item, mode)) as unknown as T;
  if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    const next: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      next[key] = deepResolve(child, mode);
    });
    return next as T;
  }
  return value;
};

export const useResolvedColors = <T,>(value: T): T => {
  const { mode } = useColorMode();
  return useMemo(() => deepResolve(value, mode), [value, mode]);
};

export const resolveColorsDeep = deepResolve;
